// Piezas compartidas de la Bitácora y Mi Estrategia (admin-only).
// Tipos de la API /journal + badges + visor de pantallazos (lightbox).

import { useState, type ReactElement } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../lib/cn';

export interface JournalScreenshot {
  id: string;
  url: string;
  timeframe: string | null;
  caption: string | null;
}

export interface JournalEntry {
  id: string;
  kind: 'analisis' | 'leccion' | 'hito' | 'sesion';
  title: string;
  summary: string;
  tags: string[];
  symbol: string | null;
  bias: string | null;
  decision: string | null;
  outcome: string | null;
  tradeRef: string | null;
  source: string;
  createdAt: string;
  updatedAt: string;
  screenshots: JournalScreenshot[];
}

export const KIND_META: Record<
  JournalEntry['kind'],
  { label: string; tone: string; dot: string }
> = {
  analisis: {
    label: 'Análisis',
    tone: 'text-info-DEFAULT bg-info-DEFAULT/10 border-info-DEFAULT/30',
    dot: 'bg-info-DEFAULT',
  },
  leccion: {
    label: 'Lección',
    tone: 'text-accent-500 bg-accent-500/10 border-accent-500/30',
    dot: 'bg-accent-500',
  },
  hito: {
    label: 'Hito',
    tone: 'text-win-DEFAULT bg-win-DEFAULT/10 border-win-DEFAULT/30',
    dot: 'bg-win-DEFAULT',
  },
  sesion: {
    label: 'Sesión',
    tone: 'text-ink-muted bg-surface-panel border-surface-border',
    dot: 'bg-ink-subtle',
  },
};

export const BIAS_TONE: Record<string, string> = {
  alcista: 'text-win-DEFAULT bg-win-DEFAULT/10 border-win-DEFAULT/30',
  bajista: 'text-loss-DEFAULT bg-loss-DEFAULT/10 border-loss-DEFAULT/30',
  neutral: 'text-ink-muted bg-surface-panel border-surface-border',
  volatil: 'text-warn-DEFAULT bg-warn-DEFAULT/10 border-warn-DEFAULT/30',
};

export const DECISION_META: Record<string, { label: string; tone: string }> = {
  operar: { label: '▶ Operar', tone: 'text-win-DEFAULT border-win-DEFAULT/40' },
  observar: { label: '◉ Observar', tone: 'text-info-DEFAULT border-info-DEFAULT/40' },
  esperar: { label: '◷ Esperar', tone: 'text-warn-DEFAULT border-warn-DEFAULT/40' },
};

export function KindBadge({ kind }: { kind: JournalEntry['kind'] }): ReactElement {
  const m = KIND_META[kind] ?? KIND_META.sesion;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full border text-[11px] font-semibold uppercase tracking-wider',
        m.tone,
      )}
    >
      <span className={cn('w-1.5 h-1.5 rounded-full', m.dot)} />
      {m.label}
    </span>
  );
}

export const fmtFecha = (iso: string): string =>
  new Intl.DateTimeFormat('es-CL', {
    timeZone: 'America/Santiago',
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));

// ── Lightbox: visor de pantallazos a pantalla completa ──────────────────────
export function Lightbox({
  shots,
  index,
  onClose,
  onMove,
}: {
  shots: JournalScreenshot[];
  index: number;
  onClose: () => void;
  onMove: (next: number) => void;
}): ReactElement | null {
  if (!shots.length) return null;
  const shot = shots[Math.max(0, Math.min(index, shots.length - 1))]!;
  return createPortal(
    <div
      className="fixed inset-0 z-[80] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <button
        className="absolute top-4 right-4 text-white/70 hover:text-white text-2xl leading-none p-2"
        onClick={onClose}
        aria-label="Cerrar"
      >
        ✕
      </button>
      {shots.length > 1 && (
        <>
          <button
            className="absolute left-2 md:left-6 text-white/60 hover:text-white text-3xl p-3"
            onClick={(e) => {
              e.stopPropagation();
              onMove((index - 1 + shots.length) % shots.length);
            }}
            aria-label="Anterior"
          >
            ‹
          </button>
          <button
            className="absolute right-2 md:right-6 text-white/60 hover:text-white text-3xl p-3"
            onClick={(e) => {
              e.stopPropagation();
              onMove((index + 1) % shots.length);
            }}
            aria-label="Siguiente"
          >
            ›
          </button>
        </>
      )}
      <figure
        className="max-w-[92vw] max-h-[88vh] flex flex-col items-center gap-3"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={shot.url}
          alt={shot.caption ?? 'Pantallazo'}
          className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl"
        />
        <figcaption className="text-white/70 text-xs font-mono flex items-center gap-3">
          {shot.timeframe && (
            <span className="px-2 py-0.5 rounded bg-white/10 uppercase">{shot.timeframe}</span>
          )}
          {shot.caption}
          <span className="text-white/40">
            {index + 1} / {shots.length}
          </span>
        </figcaption>
      </figure>
    </div>,
    document.body,
  );
}

// Grilla de miniaturas que abre el lightbox.
export function ScreenshotGrid({ shots }: { shots: JournalScreenshot[] }): ReactElement | null {
  const [open, setOpen] = useState<number | null>(null);
  if (!shots.length) return null;
  return (
    <>
      <div className="flex flex-wrap gap-2 mt-3">
        {shots.map((s, i) => (
          <button
            key={s.id}
            onClick={() => setOpen(i)}
            className="relative group rounded-md overflow-hidden border border-surface-border hover:border-accent-500/60 transition-colors"
            title={s.caption ?? 'Ver pantallazo'}
          >
            <img src={s.url} alt={s.caption ?? 'Pantallazo'} className="h-20 w-32 object-cover" loading="lazy" />
            {s.timeframe && (
              <span className="absolute bottom-1 right-1 px-1.5 py-0.5 rounded bg-black/70 text-white text-[10px] font-mono uppercase">
                {s.timeframe}
              </span>
            )}
            <span className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
          </button>
        ))}
      </div>
      {open !== null && (
        <Lightbox shots={shots} index={open} onClose={() => setOpen(null)} onMove={setOpen} />
      )}
    </>
  );
}
